import type { RateMultiplier } from '@/types/RateMultiplier';

/** Builds the rate ladder used by offset-tier controls.
 *
 *  Mirrors the backend rate_ladder CTE in shift_short_turn_view.sql for OT tiers:
 *
 *    hasPerShift := EXISTS (any row with defaultPerShiftThresholdHours IS NOT NULL)
 *    ladder := filter rows on (hasPerShift ? per-shift IS NOT NULL : per-week IS NOT NULL)
 *    ordered by threshold ascending.
 *
 *  Then unconditionally prepends STRAIGHT_TIME as rank 1 if it exists in the
 *  input. The backend rate_ladder CTE excludes ST when it has no threshold,
 *  but the actual offset calculation (calculate_gross_with_offset_start)
 *  iterates temp_prevailing_rates which includes ST intervals — ST hours
 *  do receive the offset. Including ST here:
 *    - matches the user's mental model (ST is the implicit base tier)
 *    - keeps the stepper max (ladder.length - 1) usable when ST has no
 *      threshold (otherwise a 3-tier user ladder reduces to 2 OT tiers and
 *      the stepper clamps to value 1 with no preview signal)
 *    - reflects what actually happens to ST hours when offset is applied
 */
export function buildPreviewLadder(rateMultipliers: RateMultiplier[]): RateMultiplier[] {
  const hasPerShift = rateMultipliers.some(m => m.defaultPerShiftThresholdHours != null);
  const filtered = rateMultipliers.filter(m =>
    hasPerShift
      ? m.defaultPerShiftThresholdHours != null
      : m.defaultPerWeekThresholdHours != null,
  );
  const sorted = [...filtered].sort((a, b) => {
    const aT = hasPerShift ? a.defaultPerShiftThresholdHours! : a.defaultPerWeekThresholdHours!;
    const bT = hasPerShift ? b.defaultPerShiftThresholdHours! : b.defaultPerWeekThresholdHours!;
    return aT - bT;
  });
  const st = rateMultipliers.find(m => m.code === 'STRAIGHT_TIME');
  if (st && !sorted.some(m => m.code === 'STRAIGHT_TIME')) {
    return [st, ...sorted];
  }
  return sorted;
}
